import {
  campaignIdMappings,
  campaignNameToDetailMappings,
  campaignUtmSourceMappings,
  PLAN_TYPES,
  prefillMappings,
} from "src/utils/Constants";
import { isUUID } from "src/utils/uuid";
import {
  setUserIdentified,
  resetRequestLegalReviewFailureReason,
  setRequestLegalReviewAsideDetails,
  resetStoreReferralStatus,
  getUserMetadata,
  storeReferral,
} from "src/store/slices/onboardingSlice";
import { CONSULTATION_NO_DETAILS_FALLBACK } from "src/utils/consultationDescription";

import { BUILD_NUMBER } from "src/buildInfo";
import { openCheckoutForEntry, resolveConsultationAction } from "src/utils/tierActions";
import { openWithEntry } from "src/store/slices/checkoutFlowSlice";
import { readConsultationDescription } from "src/utils/checkoutPersistence";
import { Plan } from "src/domain/plan";
import { isLegalPill, findConsultationOfferCarrier } from "src/utils/lawyerHandoff";

import applicationStore from "src/store/RootStore";

import {
  FolderLock,
  Briefcase,
  Bank,
  Building,
  Certificate,
  Receipt,
  HandDeposit,
  Note,
  Users,
  Medal,
  Pen,
  GitDiff,
  Cpu,
  Signature,
  LockKey,
  CalendarDots,
  Scroll,
  Copyright,
  FileMagnifyingGlass,
  HouseLine,
  Key,
  Handshake,
  ChartLineUp,
  Clipboard,
  Scales,
  Gavel,
  Trademark,
  Lock,
  Envelope,
  Warning,
} from "@phosphor-icons/react";
import {
  getAskThreadMessages,
  loadThreadTryPage,
  resetInvalidThreadData,
  resetThreadData,
  setAwaitingResponse,
  setCreationInProgress,
  setMessagingDisabled,
  setNewThreadMessage,
  setResetChatbox,
  setButtonClickTypeForTrigger,
  showBlockedThreadWarnPopup,
  setShouldTriggerAIResponse,
} from "src/store/slices/messageSlice";
import { resetChatPage, resetPaymentStatus } from "src/store/slices/chatSlice";
import {
  generateDownloadDraftUrl,
  resetData,
  resetReferenceDocDownloadUrl,
  setDownloadingDocumentThreadId,
} from "src/store/slices/threadsSlice";
import { setSelectedParties } from "src/store/slices/HomeSlice";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { clearInhouseAnonymousId, getInhouseAnonymousIdFromCookie } from "src/utils/anonymousId";
import { clearWebsiteThreadIds } from "src/utils/websiteThreadCookie";
import { createThreadWithoutMessage } from "./chat-page/components/chatboxHelpers";
import {
  trackPostHogEvent,
  identifyPostHogUser,
  getPostHogIdentity,
  resetPostHogUser,
  setPostHogPersonProperties,
} from "src/services/posthog";
import { trackGAEvent, identifyGAUser } from "src/services/googleAnalytics";
import { getStoredInhouseAnonymousId, isAnalyticsReady, queueEvent } from "src/services/analyticsInit";
import { trackEventServerSide } from "src/services/events";
import { readCometToken } from "src/utils/cometToken";
import { updateTitle } from "src/store/slices/threadsSlice";
import { toast } from "react-toastify";
import {
  BUTTON_ROLES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  BUTTON_TYPES,
  MODAL_NAMES,
} from "src/utils/EventConstants";
import { trackCustomPixelEvent } from "src/services/facebookPixel";
export const malformedGptDesign = (
  <>
    <div className="flex flex-col gap-[20px] justify-centre items-center w-full md:w-[679px]">
      <div className="flex flex-row gap-[12px] justify-centre items-center border-[#A2000933] bg-[#FFF6F5] p-[15px] border-[1px] rounded-[8px]">
        <div className="flex my-auto">
          <Warning size={19.5} className="text-[#A20009] my-auto" />
        </div>
        <p className="text-[16px] font-sans my-auto">
          There was an issue with the link generated on ChatGPT. Please open the ChatGPT conversation where this link
          was created and ask it to generate a new one.
        </p>
      </div>
      <p>
        If the problem persists, reach out to <span className="font-medium">support@inhouse.ai</span>
      </p>
    </div>
  </>
);

export function extractErrors(response) {
  const errors = {
    fieldErrors: {},
    genericErrors: [],
  };
  if (response && (response.status === 404 || response?.response?.status === 404)) {
    errors.genericErrors.push("The requested resource was not found.");
  }
  if (response && (response.status >= 500 || response?.response?.status >= 500)) {
    errors.genericErrors.push("Something went wrong. Please try again later.");
  } else if (
    response &&
    response.name &&
    response.name === "AxiosError" &&
    (response.status !== 422 || response?.response?.status !== 422)
  ) {
    errors.genericErrors.push(response.message);
  }
  if (response && response?.response?.status === 422) {
    const detail = response.response.data.detail;
    if (detail && detail.length > 0) {
      detail.forEach((error) => {
        if (error.loc && error.loc.length > 0) {
          if (!errors.fieldErrors[error.loc[1]]) {
            errors.fieldErrors[error.loc[1]] = [];
          }
          errors.fieldErrors[error.loc[1]].push(error.msg);
        } else {
          errors.genericErrors.push(error.msg);
        }
      });
    }
  } else {
    if (response && response.data && response.data.errors) {
      response.data.errors.forEach((error) => {
        if (error.field) {
          if (!errors.fieldErrors[error.field]) {
            errors.fieldErrors[error.field] = [];
          }
          errors.fieldErrors[error.field].push(error.message);
        } else {
          errors.genericErrors.push(error.message);
        }
      });
    }
  }

  return errors;
}

export const trackAnalytics = (event, data, metadata) => {
  data = data || {};
  if (localStorage.getItem("is_test_account") === "true") {
    data["is_test_account"] = true;
    return;
  }
  const storedId = getStoredInhouseAnonymousId();
  const cookieId = getInhouseAnonymousIdFromCookie();
  const finalId = storedId || cookieId;

  const state = applicationStore.getState();
  if (!metadata) {
    metadata = state?.onboarding?.userMetadata;
  }
  data["user_id"] = metadata?.user_id || metadata?.id || data["user_id"];
  if (data["user_id"] === null || data["user_id"] === undefined) {
    data["user_id"] = finalId;
  }

  data["user_email"] = data["user_email"] || localStorage.getItem("email") || ""

  // Identity traits on every event. The caller's data wins, and a trait that
  // is not known yet is left out rather than sent as undefined.
  const identityTraits = {
    [EVENT_PARAMETERS.FIRST_NAME]: metadata?.first_name,
    [EVENT_PARAMETERS.LAST_NAME]: metadata?.last_name,
    [EVENT_PARAMETERS.EMAIL_ADDRESS]: metadata?.email || state?.homeslice?.email || localStorage.getItem("email"),
  };
  Object.entries(identityTraits).forEach(([key, value]) => {
    if (value && data[key] === undefined) {
      data[key] = value;
    }
  });
  data[EVENT_PARAMETERS.APP_BUILD_NUMBER] = BUILD_NUMBER;

  // Always include inhouse_anonymous_id
  data["inhouse_anonymous_id"] = finalId;

  // Shared event_id so the server-side relay copy of this event can be
  // deduped against the browser-side copy at the destination ad platform.
  data["event_id"] = generateUUIDv4();

  // Cometly attributes conversions by this token, so every destination gets it,
  // not only the browser PostHog copy.
  const cometToken = readCometToken();
  if (cometToken) {
    data[EVENT_PARAMETERS.COMET_TOKEN] = cometToken;
  }

  // If analytics not ready, queue the event
  if (!isAnalyticsReady() && queueEvent("track", event, data)) {
    return; // Event queued, will be flushed later
  }

  const track = async () => {
    // Track to Segment
    try {
      const analytics = await getAnalytics();
      if (metadata?.user_id) {
        analytics.identify(metadata.user_id);
      }
      analytics.track(event, data);
    } catch (err) {
      ErrorLoggingV2({ error: "Unable to track analytics", message: JSON.stringify(err) }, "analytics_tracking_error");
    }

    // Track to PostHog
    try {
      await trackPostHogEvent(event, data);
    } catch (err) {
      ErrorLoggingV2(
        { error: "Unable to track PostHog analytics", message: JSON.stringify(err) },
        "posthog_tracking_error",
      );
    }

    // Track to Google Analytics
    try {
      trackGAEvent(event, data);
    } catch (err) {
      ErrorLoggingV2(
        { error: "Unable to track Google Analytics event", message: JSON.stringify(err) },
        "ga_tracking_error",
      );
    }

    try {
      trackCustomPixelEvent(event, data);
    }
    catch (err) {
      ErrorLoggingV2(
        { error: "Unable to track pixel event", message: JSON.stringify(err) },
        "ga_tracking_error",
      );
    }
    // Relay server-side to backend-core-v2 -> Stape, so ad blockers stripping
    // the calls above don't cause this conversion to go unreported.
    try {
      await trackEventServerSide({
        event_id: data.event_id,
        event_name: event,
        occurred_at: new Date().toISOString(),
        inhouse_anonymous_id: finalId,
        user_id: data.user_id,
        page: {
          url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        },
        properties: data,
      });
    } catch (err) {
      ErrorLoggingV2(
        { error: "Unable to relay event to backend", message: JSON.stringify(err) },
        "event_relay_error",
      );
    }
  };
  track();
};

export const clearLocalStorageKeys = () => {
  localStorage.clear();
};

export const generateNewAnonymousId = (override) => {
  // Always check for existing ID first
  const existingId = getInhouseAnonymousIdFromCookie();
  if (existingId) {
    const cleanId = getCleanSegmentId(existingId);
    return cleanId;
  }

  if (!override) {
    // Check segment-anon-id as fallback
    const segmentAnonId = localStorage.getItem("segment-anon-id");
    if (segmentAnonId) {
      const cleanId = getCleanSegmentId(segmentAnonId);
      // localStorage.setItem("ajs_anonymous_id", cleanId);
      return cleanId;
    }
  }

  // Only reset analytics and generate new ID if no ID exists
  window?.analytics?.reset();
  var id = generateUUIDv4();
  // console.log("[HELPERS.generateNewAnonymousId] Generated new UUID:", id);
  window?.analytics?.setAnonymousId(id);
  // localStorage.setItem("ajs_anonymous_id", id);
  // localStorage.setItem("segment-anon-id", id);
  localStorage.setItem("segment_id_generated_at", new Date().toISOString());
  localStorage.setItem("segment_id_source", "helpers.generateNewAnonymousId");
  // console.log("[HELPERS.generateNewAnonymousId] Stored new ID in localStorage");
  return id;
};

export const clearTokens = () => {
  try {
    deleteAllCookies();
    localStorage.removeItem("accessToken2");
    localStorage.removeItem("refreshToken2");
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
    localStorage.removeItem("client_details");
    localStorage.removeItem("lawyer_info");
    localStorage.removeItem("showForm");
    localStorage.removeItem("threadMessages");
    localStorage.removeItem("reference_doc");
    localStorage.removeItem("questions");
    localStorage.removeItem("document");
    localStorage.clear();
    // Note: NOT removing anonymous IDs here - only on explicit logout
  } catch (e) {
    // console.log(e);
  }
};

// The last identifyAnalytics() call in this page life, so a reset that lands
// after it can identify the signed-in user again.
let lastIdentifyArgs = null;

export const logoutUser = () => {
  try {
    // Clear all tokens and user data
    clearTokens();

    // Clear anonymous ID cookie on explicit logout
    clearInhouseAnonymousId();

    // Clear the shared-domain website_thread_id cookie. deleteAllCookies()
    // cannot remove it — it expires cookies without a domain attribute, which
    // never matches a cookie set with domain=<base domain>.
    clearWebsiteThreadIds();

    // PostHog's identity lives in a cookie shared with the marketing site and
    // survives localStorage.clear(). Without a reset, the next account to sign
    // in on this browser is recorded against this user's person.
    resetPostHogUser();
    lastIdentifyArgs = null;

    // Clear Clerk's __session cookie on the shared domain so the marketing
    // site's instant-redirect script doesn't redirect logged-out users.
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN || "inhouse.ai";
    document.cookie = `__session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${baseDomain}; SameSite=Lax`;
  } catch (e) {
    // console.log(e);
  }
};

const ANALYTICS_CLERK_USER_KEY = "analytics_clerk_user_id";

/**
 * Reset PostHog when it is still identified as someone other than the current
 * Clerk user, before that user's events are sent. This happens when a session
 * ends without logoutUser() (expiry, sign-out on the marketing site) or when
 * the marketing site identified the browser by its anonymous ID. PostHog never
 * merges one identified person into another, so a later identify() cannot
 * repair events already sent to the wrong person.
 *
 * Pass null when Clerk has loaded and nobody is signed in.
 */
export const reconcileAnalyticsIdentity = async (clerkUserId) => {
  try {
    const identity = await getPostHogIdentity();
    if (!identity) return;

    const anonymousId = getStoredInhouseAnonymousId() || getInhouseAnonymousIdFromCookie();
    const lastClerkUserId = localStorage.getItem(ANALYTICS_CLERK_USER_KEY);
    // A missing key says nothing about who PostHog is identified as: it is
    // missing for every signed-in user on the first load after this shipped,
    // and after anything that clears localStorage.
    const isOtherClerkUser = lastClerkUserId !== null && lastClerkUserId !== clerkUserId;
    const isStale =
      identity.isIdentified && (identity.distinctId === anonymousId || !clerkUserId || isOtherClerkUser);

    if (isStale) {
      await resetPostHogUser();
      // identifyAnalytics() skips users it has already identified; the new
      // anonymous PostHog ID needs identifying again.
      localStorage.removeItem("identifiedUser");
      // identifyAnalytics() may already have run on this page, and nothing
      // calls it again until the user's metadata changes.
      if (clerkUserId && lastIdentifyArgs) {
        identifyAnalytics(...lastIdentifyArgs);
      }
    }

    if (clerkUserId) {
      localStorage.setItem(ANALYTICS_CLERK_USER_KEY, clerkUserId);
    } else {
      localStorage.removeItem(ANALYTICS_CLERK_USER_KEY);
    }
  } catch (err) {
    ErrorLoggingV2(
      { error: "Unable to reconcile analytics identity", message: JSON.stringify(err) },
      "analytics_identity_reconcile_error",
    );
  }
};

/** Set properties on the current PostHog person, identified or not. */
export const setAnalyticsPersonProperties = async (properties) => {
  try {
    await setPostHogPersonProperties(properties);
  } catch (err) {
    ErrorLoggingV2(
      { error: "Unable to set PostHog person properties", message: JSON.stringify(err) },
      "posthog_person_properties_error",
    );
  }
};

export const identifyAnalytics = (email, userMetadata, dispatch) => {
  lastIdentifyArgs = [email, userMetadata, dispatch];
  var data = {
    email: email,
    first_name: userMetadata?.individual_info?.first_name,
    last_name: userMetadata?.individual_info?.last_name,
    organization_name: userMetadata?.entity_info?.name || "",
    state_incorporation: userMetadata?.entity_info?.state_incorporation || "",
    state_business: userMetadata?.entity_info?.primary_business_state || "",
    state_residence: userMetadata?.individual_info?.state_of_residence || "",
    account_type: userMetadata?.entity_info ? "business" : "personal",
    payment_frequency: userMetadata?.payment_frequency || "",
    assigned_lawyer_name: userMetadata?.lawyer_info?.name || "",
    assigned_lawyer_email: userMetadata?.lawyer_info?.email || "",
    subscription_type: userMetadata?.subscription_type || "",
    ...(localStorage.getItem("is_test_account") === "true" && { is_test_account: true }),
  };
  var id = userMetadata?.user_id;
  if (id) {
    var details = JSON.stringify({ id: id, ...data });
    if (localStorage.getItem("identifiedUser") === details) {
      return;
    }
    const func = async () => {
      // Identify in Segment
      try {
        const analytics = await getAnalytics();
        if (analytics) {
          analytics.identify(id, data);
        }
      } catch (err) {
        ErrorLoggingV2(
          { error: "Unable to identify user in Segment", message: JSON.stringify(err) },
          "segment_identify_error",
        );
      }

      // Identify in PostHog
      try {
        await identifyPostHogUser(id, data);
      } catch (err) {
        ErrorLoggingV2(
          { error: "Unable to identify user in PostHog", message: JSON.stringify(err) },
          "posthog_identify_error",
        );
      }

      // Identify in Google Analytics
      try {
        identifyGAUser(id, data);
      } catch (err) {
        ErrorLoggingV2(
          { error: "Unable to identify user in Google Analytics", message: JSON.stringify(err) },
          "ga_identify_error",
        );
      }

      localStorage.setItem("identifiedUser", details);
      dispatch(setUserIdentified(true));
    };
    func();
  }
};

export const getDraftIcon = (icon, size) => {
  switch (icon) {
    case "briefcase":
    case "Briefcase":
      return <Briefcase size={size} />;
    case "bank":
    case "Bank":
      return <Bank size={size} />;
    case "building":
    case "Building":
      return <Building size={size} />;
    case "certificate":
    case "Certificate":
      return <Certificate size={size} />;
    case "receipt":
    case "Receipt":
      return <Receipt size={size} />;
    case "folder":
    case "Folder":
      return <FolderLock size={size} />;
    case "Medal":
      return <Medal size={size} />;
    case "FolderLock":
      return <FolderLock size={size} />;
    case "HandDeposit":
      return <HandDeposit size={size} />;
    case "Envelope":
      return <Envelope size={size} />;
    case "Pen":
      return <Pen size={size} />;
    case "GitDiff":
      return <GitDiff size={size} />;
    case "Cpu":
      return <Cpu size={size} />;
    case "Note":
      return <Note size={size} />;
    case "Users":
      return <Users size={size} />;
    case "Signature":
      return <Signature size={size} />;
    case "LockKey":
      return <LockKey size={size} />;
    case "CalendarDots":
      return <CalendarDots size={size} />;
    case "scroll":
    case "Scroll":
      return <Scroll size={size} />;

    case "ChartLineUp":
      return <ChartLineUp size={size} />;
    case "Handshake":
      return <Handshake size={size} />;
    case "Key":
      return <Key size={size} />;
    case "Trademark":
      return <Trademark size={size} />;
    case "Clipboard":
      return <Clipboard size={size} />;
    case "HouseLine":
      return <HouseLine size={size} />;
    case "FileMagnifyingGlass":
      return <FileMagnifyingGlass size={size} />;
    case "Scales":
      return <Scales size={size} />;
    case "Gavel":
      return <Gavel size={size} />;
    case "Copyright":
      return <Copyright size={size} />;
    default:
      return <Lock size={size} />;
  }
};

export const homePageClickAction = (dispatch, metadata, email) => {
  trackAnalytics("home:talk_to_lawyer_center_bar", { email: email }, metadata);
  // depeneding on the subscription plan, we might open the legal review side-form, or the upgrade plan popup
};

export const handleAuthActionType = (authActionType, dispatch, userMetadata, threadData, email, navigate, threadId) => {
  if (!authActionType) return;
  var btn;
  const singleModalShow = applicationStore.getState().onboarding.openSinglePlanModal.show;
  const multiPlanModalShow = applicationStore.getState().onboarding.openMultiPlanModal.show;
  if (singleModalShow || multiPlanModalShow) return;
  switch (authActionType) {
    case "consult_attorney": {
      const paramsCa = new URLSearchParams(window.location.search);
      const thread_id = threadData?.id || paramsCa.get("thread_id");

      // Skip at PostAuth time (null threadData) — PostAuth navigates to
      // /chat/{thread_id}?action_type=consult_attorney and ChatPageComponent
      // will re-invoke with real threadData.
      if (!threadData?.id) break;

      localStorage.removeItem("post_auth_doc_id");
      localStorage.removeItem("post_auth_doc_title");
      localStorage.removeItem("post_auth_is_doc_unlocked");

      // Restore the full button payload saved before signup redirect.
      let savedBtn = null;
      try {
        const raw = localStorage.getItem("legal_review_btn");
        if (raw) savedBtn = JSON.parse(raw);
      } catch (_) { /* corrupted — fall through to reconstruction */ }
      localStorage.removeItem("legal_review_btn");

      if (savedBtn) {
        btn = savedBtn;
      } else {
        const { eligible_offers, referral_category, lawyer_handoff_context } = findeligibleOffers(threadData);
        btn = {
          text: "Consult Attorney",
          action: "schedule_call",
          referral_category: referral_category,
          eligible_offers: eligible_offers || {
            lawyer_consultation: "default",
            lawyer_finalization: null,
            ai_document: null,
          },
          offer: "lawyer_consultation",
          lawyer_handoff_context: lawyer_handoff_context,
        };
      }

      // INH-5289: After signin/signup, open shelf + checkout in one shot.
      // The user already clicked "Loop in your lawyer" before auth — reopening
      // the shelf alone forced a redundant second click and fired a spurious
      // CLICKED_BUTTON event.
      const postAuthAction = resolveConsultationAction(userMetadata, btn?.eligible_offers);
      const savedDescription = readConsultationDescription(thread_id) || "";
      openReferralDrawer(btn, thread_id, null, dispatch);
      dispatch(resetPaymentStatus());

      if (postAuthAction.type === "direct_checkout") {
        // Free / Pro → Counsel subscription checkout.
        dispatch(
          openWithEntry({
            entry: "consultation",
            threadId: thread_id,
            selectedTier: postAuthAction.tier,
            initialStep: "checkout",
            additionalInfo: savedDescription,
            consultationDocId: btn?.docId || null,
            consultationReferralCategory: btn?.referral_category || "business",
          }),
        );
      } else if (postAuthAction.type === "purchase_additional_consultation") {
        // Counsel / Unlimited with exhausted credits → one-time $149 purchase.
        dispatch(
          openWithEntry({
            entry: "consultation",
            threadId: thread_id,
            purchaseTarget: { kind: "one_time", transactionType: "legal_consultation" },
            initialStep: "checkout",
            additionalInfo: savedDescription,
            consultationDocId: btn?.docId || null,
            consultationReferralCategory: btn?.referral_category || "business",
          }),
        );
      } else if (postAuthAction.type === "submit_referral") {
        // Counsel / Unlimited with credits — auto-submit the referral.
        // The user already clicked "Loop in your lawyer" before auth,
        // so repeating that click would be redundant.
        const fullName = `${userMetadata?.first_name || ""} ${userMetadata?.last_name || ""}`.trim();
        const phoneDigits = (userMetadata?.phone_number || "").replace(/^\+1/, "");
        const description = savedDescription
          ? `What should the lawyer focus on: ${savedDescription}`
          : CONSULTATION_NO_DETAILS_FALLBACK;
        dispatch(storeReferral({
          name: fullName,
          email: email,
          phone: phoneDigits,
          thread_id: thread_id,
          description,
          state: userMetadata?.individual_info?.state_of_residence || "",
          referral_category: btn?.referral_category || "business",
          lawyer_consultation_offer: btn?.eligible_offers?.lawyer_consultation || "default",
          type: "consultation",
          doc_id: btn?.docId || null,
        }));
      }
      break;
    }
    case "unlock_doc": {
      const paramsUd = new URLSearchParams(window.location.search);
      const thread_id = threadData?.id || paramsUd.get("thread_id");
      if (!thread_id) break;
      const docTitleFromUrl = paramsUd.get("doc_title");
      paramsUd.delete("action_type");
      paramsUd.delete("doc_title");
      navigate(window.location.pathname + (paramsUd.toString() ? `?${paramsUd.toString()}` : ""), { replace: true });
      if (threadData.lastMessage?.message_type === "deprecated_thread_message") break;
      openCheckoutForEntry(dispatch, { entry: "locked_doc", threadId: thread_id, lockedDocTitle: docTitleFromUrl || threadData?.document?.doc_title || null });
      break;
    }
    case "attachment_limit": {
      const paramsAl = new URLSearchParams(window.location.search);
      paramsAl.delete("action_type");
      navigate(window.location.pathname + (paramsAl.toString() ? `?${paramsAl.toString()}` : ""), { replace: true });
      // Plan.isPaid() replaces the inline paid-tier set (chat-plan-adoption);
      // the legacy `court_document` value was dropped — BE migrated it away,
      // and a stray holder fails toward checkout (the safe direction here).
      if (!Plan.from(userMetadata?.subscription_type).isPaid()) {
        openCheckoutForEntry(dispatch, { entry: "attachment_limit" });
      }
      break;
    }
    default:
      // Legacy action_type values (finalize_document, finalization) are gone —
      // finalization is not a product surface under the 4-tier subscription
      // model. Unknown values still warn so we catch typos.
      console.warn(`Unknown action_type: ${authActionType}`);
      break;
  }

  // Remove the query parameter from the URL after handling
  // const url = new URL(window.location);
  // url.searchParams.delete("action_type");
  // window.history.replaceState({}, "", url);
};

export const getCookie = (name) => {
  const cookieString = document.cookie;
  const cookies = cookieString.split("; ");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].split("=");
    if (cookie[0] === name) {
      return decodeURIComponent(cookie[1]);
    }
  }
  return null; // Return null if cookie with given name is not found
};

export const setCookie = (name, value, days = 7) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/`;
};

export const extractUUID = (str) => {
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
  const match = str.match(uuidRegex);
  var uuid = match ? match[0] : null;
  return uuid;
};

export const getCleanSegmentId = (id) => {
  if (!isUUID(id)) {
    id = extractUUID(id);
  } else {
  }
  return id;
};

export const getAnalytics = async () => {
  return new Promise((resolve) => {
    const checkAnalytics = (counter = 0) => {
      if (counter === 30) {
        resolve();
        return;
      }
      if (window.analytics && window.analytics.user) {
        resolve(window.analytics);
      } else {
        setTimeout(checkAnalytics(counter + 1), 1000); // Retry after 500ms
      }
    };
    checkAnalytics();
  });
};

export const waitForAuthToken = async () => {
  return new Promise((resolve) => {
    const checkToken = (counter = 0) => {
      if (counter === 10) {
        // Max 10 attempts = 10 seconds
        resolve(null);
        return;
      }
      const token = localStorage.getItem("accessToken2");
      if (token && token !== "null" && token !== "undefined") {
        resolve(token);
      } else {
        setTimeout(() => checkToken(counter + 1), 1000); // Retry after 1 second
      }
    };
    checkToken();
  });
};

export const withAuthRetry = async (apiCall, maxRetries = 3) => {
  const makeRequest = async (retryCount = 0) => {
    let accessToken = localStorage.getItem("accessToken2");

    // If no token or retry needed, wait for token
    if (!accessToken || retryCount > 0) {
      accessToken = await waitForAuthToken();
      if (!accessToken) {
        throw new Error("Authentication token unavailable after waiting");
      }
    }

    try {
      return await apiCall(accessToken);
    } catch (error) {
      // Retry on 401 errors up to maxRetries times
      if (error.response?.status === 401 && retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay));
        return makeRequest(retryCount + 1);
      }
      throw error;
    }
  };

  return makeRequest();
};

export const getSegmentId = async (original) => {
  // First check localStorage for existing ID

  // changing this to segment-anon-id instead of ajs_anonymous_id because ajs_anonymous_id
  // is being set by segment which can be triggered multiple times in parallel from getsegmentid function call
  const existingId = localStorage.getItem("segment-anon-id");
  if (existingId && original !== existingId) {
    const cleanId = getCleanSegmentId(existingId);
    localStorage.removeItem("segment_id_generating");
    return cleanId;
  }

  // Check if generation is already in progress
  const isGenerating = localStorage.getItem("segment_id_generating");
  if (isGenerating === "true") {
    // console.log("[HELPERS.getSegmentId] ID generation already in progress, waiting...");
    // Wait for the other process to complete
    let waitCount = 0;
    while (waitCount < 20) {
      // Max wait 2 seconds
      await new Promise((resolve) => setTimeout(resolve, 100));
      const id = getInhouseAnonymousIdFromCookie();
      if (id) {
        // console.log("[HELPERS.getSegmentId] Found ID after waiting:", id);
        return getCleanSegmentId(id);
      }
      waitCount++;
    }
    // console.log("[HELPERS.getSegmentId] Wait timeout exceeded");
  }

  // Set mutex
  localStorage.setItem("segment_id_generating", "true");

  try {
    const analytics = await getAnalytics();

    var id;
    try {
      id = analytics?.user().anonymousId();
      // console.log("[HELPERS.getSegmentId] Got ID from analytics.user():", id);
      if (id) {
        // Verify this ID matches what's in localStorage before using it
        const cleanId = getCleanSegmentId(id);
        const storedId = getInhouseAnonymousIdFromCookie();

        // If there's a mismatch, prefer the stored ID to maintain consistency
        if (storedId && storedId !== cleanId) {
          // console.log("[HELPERS.getSegmentId] Segment ID mismatch detected, using stored ID");
          localStorage.removeItem("segment_id_generating");
          return getCleanSegmentId(storedId);
        }

        // I am removing the set-option  from here. the only time the localstorage would be set would be the time when
        // websocket uses it. otherwise there will always be a mismatch.

        // Store the ID if it's valid and not already stored
        // localStorage.setItem("ajs_anonymous_id", cleanId);
        // localStorage.setItem("segment-anon-id", cleanId);
        localStorage.setItem("segment_id_generated_at", new Date().toISOString());
        localStorage.setItem("segment_id_source", "helpers.getSegmentId");
        localStorage.removeItem("segment_id_generating");
        return cleanId;
      }
    } catch (e) {
      // console.log("[HELPERS.getSegmentId] Unable to get anonymousId from segment", e);
      ErrorLoggingV2(
        { error: "Unable to get anonymousId from segment", message: JSON.stringify(e) },
        "segment_id_not_found",
      );
    }

    // Only generate new ID if absolutely necessary
    // console.log("[HELPERS.getSegmentId] No ID found anywhere, generating new anonymousId");
    id = getCleanSegmentId(generateNewAnonymousId());
    localStorage.removeItem("segment_id_generating");
    return id;
  } catch (error) {
    // console.log("[HELPERS.getSegmentId] Unexpected error:", error);
    localStorage.removeItem("segment_id_generating");
    throw error;
  }
};

export const startNewChat = (dispatch, navigate) => {
  if (window.location.pathname === "/try") {
    resetAndStartNewChat(dispatch);
    navigate("/try");
    window.location.reload();
  } else {
    navigate("/home");
    window.location.reload();
  }
};

export const resetAndStartNewChat = (dispatch) => {
  dispatch(resetThreadData());
  localStorage.removeItem("threadData");
  localStorage.removeItem("threadType");
  localStorage.removeItem("userConfig");
  localStorage.removeItem("threadMessages");
  localStorage.removeItem("questions");
  localStorage.removeItem("document");
  localStorage.removeItem("documentId");
  localStorage.removeItem("reference_doc");
  deleteAllCookies();
  dispatch(resetRequestLegalReviewFailureReason());
  dispatch(resetChatPage());
  dispatch(resetStoreReferralStatus());
  dispatch(resetData());
  dispatch(resetThreadData());
  dispatch(setSelectedParties({}));
  dispatch(setAwaitingResponse(false));
  dispatch(setMessagingDisabled(false));
  dispatch(resetReferenceDocDownloadUrl());
};

// Legacy `upgradeButtonClicked`, `paymentCancellationAction`, and
// `openCheckoutModal` were removed in subscription-tiers-rewrite Group 9 +
// 10. They wrote URL-based `/{flow}/checkout/:id` redirects, dispatched the
// old single/multi plan modals, and branched on `eligible_offers.lawyer_finalization`
// — all of which are dead under the modal-driven 4-tier model. New callers
// dispatch `openWithEntry` / `openCheckoutForEntry` instead.

export { formatText } from "src/utils/textFormatters";
export const getFirstTenkWords = (message) => {
  if (!message) return "";
  const trimmedMsg = message.trim();
  const words = trimmedMsg.split(/\s+/).slice(0, 10000).join(" ");
  return words;
};

export const countInputMessageWords = (s) => {
  return s.trim().split(/\s+/).length;
};

export const deleteCookie = (cookieToDelete) => {
  document.cookie.split("; ").forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    if (name === cookieToDelete) {
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
  });
};

export const deleteAllCookies = () => {
  document.cookie.split("; ").forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  });
};

export const generateUUIDv4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Debug function to check segment ID state
export const debugSegmentIdState = () => {
  // console.log("=== SEGMENT ID DEBUG STATE ===");
  // console.log("Current Time:", new Date().toISOString());
  // console.log("localStorage.ajs_anonymous_id:", getInhouseAnonymousIdFromCookie());
  // console.log("localStorage.segment-anon-id:", localStorage.getItem("segment-anon-id"));
  // console.log("localStorage.segment_id_source:", localStorage.getItem("segment_id_source"));
  // console.log("localStorage.segment_id_generated_at:", localStorage.getItem("segment_id_generated_at"));
  // console.log("localStorage.segment_id_generating:", localStorage.getItem("segment_id_generating"));

  try {
    if (window.analytics && window.analytics.user) {
      // console.log("analytics.user().anonymousId():", window.analytics.user().anonymousId());
    } else {
      // console.log("analytics.user() not available");
    }
  } catch (e) {
    // console.log("Error getting analytics.user():", e);
  }

  // console.log("Cookie ajs_anonymous_id:", getCookie("ajs_anonymous_id"));
  // console.log("==============================");
};

// Add to window for easy debugging
if (typeof window !== "undefined") {
  window.debugSegmentIdState = debugSegmentIdState;
}

export const sendWsMessage = ({
  message,
  dispatch,
  socketRef,
  sendMessage,
  scroll,
  onSuccess,
  onFailure,
  awaitingCounter = 0,
}) => {
  if (awaitingCounter > 20) {
    ErrorLoggingV2(
      {
        error: "Failed to send message after multiple attempts",
        errorInfo: {
          message: message,
          type: "sendWsMessage",
          awaitingCounter: awaitingCounter,
        },
        anonymous_user_id: getInhouseAnonymousIdFromCookie(),
        email: localStorage.getItem("email"),
      },
      "sendWsMessage-error",
    );
    onFailure();
    return;
  }
  if (socketRef?.current?.readyState !== 1) {
    awaitingCounter++;
    setTimeout(() => {
      return sendWsMessage({
        message: message,
        dispatch: dispatch,
        socketRef: socketRef,
        sendMessage: sendMessage,
        scroll: scroll,
        onSuccess: onSuccess,
        onFailure: onFailure,
        awaitingCounter: awaitingCounter,
      });
    }, 1000);
  } else {
    sendMessage(message, scroll);
    onSuccess();
  }
};

export const createGptThread = (
  dispatch,
  queryParams,
  setPage,
  setIsMalformedUrl,
  setLoadingText,
  utmData,
  newThreadCreateEventName,
  createMessage,
  utmSource,
  socketRef,
  sendMessage,
  setShimmerNotAllowed,
  toast,
  navigate,
) => {
  if (!queryParams.get("gpt_context_id")) {
    setPage("landing");
    return;
  }
  var threadid = queryParams.get("gpt_context_id");
  if (!newThreadCreateEventName) {
    newThreadCreateEventName = "new-thread-create";
  }
  setIsMalformedUrl(false);
  trackAnalytics(newThreadCreateEventName, {
    anonymous_user_id: getInhouseAnonymousIdFromCookie(),
    ...utmData,
  });
  var message = createMessage("chat", "create-thread", {
    // gpt_context_type: "ask",
    gpt_context_id: threadid,
    // utm_source: utmSource,
  });
  sendWsMessage({
    message: message,
    dispatch: dispatch,
    socketRef: socketRef,
    sendMessage: sendMessage,

    onSuccess: () => {
      // console.log("successfully sent gpt-thread-create-request");
    },
    onFailure: () => {
      dispatch(setMessagingDisabled(false));
      dispatch(setAwaitingResponse(false));
      setShimmerNotAllowed(true);
      toast.error("Failed to load chat. Please try again");
      ErrorLoggingV2(
        {
          error: "Failed to load gpt-thread-create-request",
          errorInfo: {
            threadId: threadid,
            type: "gpt_thread_create_request",
            message: "Failed to load gpt-thread-create-request",
          },
          anonymous_user_id: getInhouseAnonymousIdFromCookie(),
        },
        "gpt-thread-create-request-error",
      );
      resetChatTryPage(setIsMalformedUrl, dispatch, setPage, navigate);
    },
  });
};

export const loadTryPageGptThread = (queryParams, setPage, setIsMalformedUrl, setLoadingText, utmData, dispatch) => {
  // case: gpt based chat
  var threadid = queryParams.get("gpt_context_id");

  // if (!isUUID(threadid)) {
  //   setPage("loading");
  //   setIsMalformedUrl(true);
  //   setLoadingText(malformedGptDesign);
  //   trackAnalytics("invalid-gpt-url", {
  //     anonymous_user_id: localStorage.getItem("segment-anon-id"),
  //     ...utmData,
  //   });

  //   return;
  // }

  if (!threadid) {
    setPage("landing");
  } else {
    // list all the messages of the thread
    dispatch(getAskThreadMessages({ threadId: threadid, dispatchSource: "helpers-loadThread" }));
  }
};

export const loadTryPageWithoutQueryParams = (
  dispatch,
  inputMessage,
  setInputMessage,
  utmData,
  createMessage,
  socketRef,
  sendMessage,
  setChatId,
  loadingText,
  setPage,
) => {
  var cookieFromHomepage = getCookie("userInput");
  var storageCookieFromHomepage = getCookie("storageId");
  var recentlyReadRandomAccessId = localStorage.getItem("alreadyReadRandomAccessId");
  var randomAccessCookie = getCookie("randomAccessId");
  if (cookieFromHomepage && randomAccessCookie !== recentlyReadRandomAccessId) {
    localStorage.setItem("alreadyReadRandomAccessId", randomAccessCookie);
    setInputMessage(cookieFromHomepage);
    setPage("landing");
  } else {
    const queryParams = new URLSearchParams(window.location.search);
    var threadId = queryParams.get("thread_id");
    if (threadId) {
      if (!isUUID(threadId)) {
        threadId = extractUUID(threadId);
      }
      dispatch(getAskThreadMessages({ threadId, dispatchSource: "helpers-loadThreadFromUrl" })).then(() => {
        const currentState = applicationStore.getState().messageSlice;
        const storedLastMessage = currentState.threadData.lastMessage;
        if (storedLastMessage?.is_user_message) {
          dispatch(setShouldTriggerAIResponse(true));
        }
      });
      setPage("chat");
    } else {
      setPage("landing");
    }
  }
};

export const createUtmThread = async (
  queryParams,
  setPage,
  setLoadingText,
  createMessage,
  sendMessage,
  socketRef,
  dispatch,
  setCreationInProgress,
  newThreadCreateEventName,
  toast,
) => {
  var message;

  var utm_datum = {
    utm_campaign: queryParams.get("utm_campaign"),
    utm_source: queryParams.get("utm_source"),
    utm_medium: queryParams.get("utm_medium"),
    thread_id: queryParams.get("thread_id"),
  };

  if (queryParams.get("utm_source") === "draft-documents") {
    setPage("loading");
    message = createMessage("draft", "create-thread", {
      message: queryParams.get("query"),
      reference_doc_storage_id: null,
      draft_type_id: null,
      utm_source: "draft-documents",
    });
  } else if (queryParams.get("utm_campaign")) {
    const creationInProgress = applicationStore.getState().messageSlice.creationInProgress;
    const thread = applicationStore.getState().messageSlice.threadData;
    if (creationInProgress || thread.id) {
      return;
    }
    setPage("loading");
    setLoadingText("Initiating new chat");
    var utm_campaign = queryParams.get("utm_campaign").toLowerCase();
    var utm_campaign_split = utm_campaign.split("_");
    var utm_campaign_name;
    try {
      utm_campaign_name = utm_campaign_split?.[utm_campaign_split.length - 3]?.toLowerCase();
    } catch (e) {
      utm_campaign_name = utm_campaign_split[0]?.toLowerCase();
    }

    var campaignId = campaignIdMappings[utm_campaign];
    var campaignUtmSource = campaignUtmSourceMappings[utm_campaign];
    if (campaignId === "others") {
      // case :  when campaignId is not present in the mapping
      message = createMessage("draft", "create-thread", {
        message: campaignNameToDetailMappings[utm_campaign_name],
        reference_doc_storage_id: null,
        draft_type_id: null,
        utm_source: campaignUtmSource,
      });
    } else if (!campaignId) {
      // case : when campaign is unknown
      message = createMessage("draft", "create-thread", {
        message: `Draft a ${utm_campaign_name}`,
        reference_doc_storage_id: null,
        draft_type_id: null,
        utm_source: campaignUtmSource,
      });
    } else {
      // case: when campaignid is present in the mapping
      var prefillMessage = prefillMappings[campaignUtmSource];

      message = createMessage("draft", "create-thread", {
        message: prefillMessage,
        reference_doc_storage_id: null,
        draft_type_id: prefillMessage ? null : campaignId,
        utm_source: campaignUtmSource,
      });
    }
  }
  if (!newThreadCreateEventName) {
    newThreadCreateEventName = "new-thread-create";
  }
  dispatch(setCreationInProgress(true));
  trackAnalytics(newThreadCreateEventName, { anonymous_user_id: await getSegmentId(), ...utm_datum });
  localStorage.setItem("utm_campaign", utm_campaign);
  sendWsMessage({
    message: message,
    dispatch: dispatch,
    socketRef: socketRef,
    sendMessage: sendMessage,

    onSuccess: () => {
      // console.log("successfully created thread on try page");
    },
    onFailure: () => {
      dispatch(setMessagingDisabled(false));
      dispatch(setAwaitingResponse(false));
      localStorage.removeItem("recentlyReadRandomAccessId");
      localStorage.removeItem("utm_campaign");
      dispatch(setCreationInProgress(false));
      setPage("landing");
      toast.error("Failed to create thread. Please refresh the page and try again");
      ErrorLoggingV2(
        {
          error: "Failed to create thread on try page",
          errorInfo: {
            threadId: null,
            type: "create_thread",
            message: message,
          },
          utm_data: {
            ...utm_datum,
          },
          anonymous_user_id: getInhouseAnonymousIdFromCookie(),
        },
        "try-page-create-thread-error",
      );
    },
  });
};

export const resetChatTryPage = (setIsMalformedUrl, dispatch, setPage, navigate) => {
  setIsMalformedUrl(false);
  dispatch(resetThreadData());
  dispatch(resetInvalidThreadData());
  var recentlyReadRandomAccessId = localStorage.getItem("alreadyReadRandomAccessId");
  setPage("landing");
  resetAndStartNewChat(dispatch);
  dispatch(setCreationInProgress(false));

  // Anonymous ID is stored in cookies (inhouse_anonymous_id), not localStorage
  // Cookie will persist through localStorage.clear()
  localStorage.clear();
  if (recentlyReadRandomAccessId) {
    localStorage.setItem("alreadyReadRandomAccessId", recentlyReadRandomAccessId);
  }
  dispatch(resetThreadData());
  dispatch(setResetChatbox(true));
  dispatch(setMessagingDisabled(false));
  dispatch(setAwaitingResponse(false));
  navigate("/try");
};

export const getHeaderAndPlaceholder = (queryParams) => {
  var mapping;
  try {
    // // console.log(process.env.REACT_APP_HEADER_PLACEHOLDER_MAPPING);
    mapping = JSON.parse(process.env.REACT_APP_HEADER_PLACEHOLDER_MAPPING);
  } catch (e) {
    mapping = {
      nda: {
        header: "Draft an NDA that actually protects your secrets",
        placeholder: "Tell me more about your NDA",
      },
    };
  }

  var details = mapping[queryParams.get("ui_theme")];
  if (!details) {
    details = {
      header: "How can we help you?",
      placeholder: "Ask me any legal questions here",
    };
  }
  return details;
};

export const openReferralDrawer = (btn, chatId, messageId, dispatch) => {
  const category = btn.referral_category || null;
  const offer = btn?.eligible_offers?.lawyer_consultation;
  dispatch(
    setRequestLegalReviewAsideDetails({
      show: true,
      referralCategory: category,
      referralCategoryText: btn.text,
      threadId: chatId,
      offer: offer,
      btn: btn,
    }),
  );
};

const isDocumentUnlocked = (threadId) => {
  const threadData = applicationStore.getState().messageSlice.threadData;
  if (threadData?.id === threadId) {
    const document = threadData?.messages.find((msg) => msg.message_type === "document_generated");
    if (document) {
      return true;
    }
  }
  const generatedDocs = applicationStore.getState().threads.generatedDocs;
  const threadGroup = generatedDocs?.find((item) => item.thread_id === threadId);
  if (threadGroup) {
    // If any document in the thread is locked, all are considered locked
    const anyLocked = threadGroup.documents?.some((d) => d.is_document_locked);
    return !anyLocked;
  }
  return false;
};

/**
 * handleLegalReviewShelfOpen — universal shelf-mount entry point.
 *
 * Per amended legal-review-access spec (2026-05-23, tasks 11.E.2 + 11.E.3):
 * every "Loop in Counsel" CTA mounts the LegalReviewShelf as the universal
 * first stop. The tier × credits matrix is resolved on the in-shelf submit
 * click via `tierActions.resolveConsultationAction` (Consultation.js
 * `handleLoopInCounsel`), NOT on the initial CTA click.
 *
 * The `userMetadata` parameter is retained for caller-site compatibility but
 * is no longer consulted here.
 */
// eslint-disable-next-line no-unused-vars
export const handleLegalReviewShelfOpen = (dispatch, btn, chatId, userMetadata) => {
  openReferralDrawer(btn, chatId, null, dispatch);
};

/**
 * handleLegalReviewButtonClick — universal shelf-mount entry point with
 * thread-state guards + analytics. Mirrors `handleLegalReviewShelfOpen`'s
 * routing (always opens the shelf; submit-click does the matrix routing).
 */
export const handleLegalReviewButtonClick = async (btn, dispatch, userMetadata, chatId, docId = null) => {
  void docId;

  const lastMessageType = applicationStore.getState().messageSlice.threadData?.lastMessage?.message_type;
  if (lastMessageType === "update_loading_message") {
    return;
  }
  if (lastMessageType === "deprecated_thread_message") {
    dispatch(showBlockedThreadWarnPopup(true));
    return;
  }

  dispatch(resetPaymentStatus());
  // Event 102: keep the existing click analytics regardless of routing path.
  trackAnalytics(
    EVENT_NAMES.CLICKED_BUTTON,
    {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
      [EVENT_PARAMETERS.THREAD_ID]: chatId,
      [EVENT_PARAMETERS.BUTTON_TEXT]: btn.text,
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      [EVENT_PARAMETERS.BUTTON_ROLE]: btn.text ? BUTTON_ROLES.CONTACT_LAWYER : BUTTON_ROLES.REQUEST_DOCUMENT_REVIEW,
    },
    userMetadata,
  );

  openReferralDrawer(btn, chatId, null, dispatch);
};

// `isCourtDocument`, `startFinalizationCheckout`, `showSingleModal`, and
// `showMultiModal` were removed in subscription-tiers-rewrite Groups 9 + 10.
// court_document is now a server-side migrated subscription_type only
// (Group 9.2), finalization is no longer a product surface (Group 9.3), and
// the single/multi plan modals are replaced by `CheckoutFlowModal`.

export const updateThreadTitle = (dispatch, thread, duration, newTitle, setLoading) => {
  if (newTitle.trim().length >= 2 && newTitle.trim().length <= 50) {
    setLoading(true);
    dispatch(updateTitle({ id: thread?.id, title: newTitle, duration: duration }));
  } else {
    toast.error("Thread title must be between 2 and 50 characters");
  }
};

export const downloadDocument = (dispatch, document, data, userMetadata) => {
  // Event 103:
  trackAnalytics(
    EVENT_NAMES.CLICKED_BUTTON,
    {
      [EVENT_PARAMETERS.THREAD_ID]: data?.threadId,
      [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      [EVENT_PARAMETERS.BUTTON_TEXT]: "Download as " + data?.type,
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.DOWNLOAD_DOCUMENT,
    },
    userMetadata,
  );
  dispatch(generateDownloadDraftUrl({ threadId: data?.threadId, type: data?.type }));
  dispatch(setDownloadingDocumentThreadId(data?.threadId));
};

export const isThreadPage = () => {
  var url = window.location.pathname;
  return url.includes("/ask/") || url.includes("/draft/") || url.includes("/chat/") || url.includes("/try");
};

export const findeligibleOffers = (threadData, generatedDocs) => {
  var eligible_offers = {
    ai_document: null,
    lawyer_consultation: null,
  };
  var referral_category = "business";
  var lawyer_handoff_context = null;
  if (threadData?.messages?.length > 0) {
    let hasDoc = threadData?.messages.find(
      (msg) => msg.message_type === "document_generated" || msg.message_type === "locked_document_generated",
    );
    if (hasDoc) {
      const doc = generatedDocs?.find((doc) => doc.thread_id === threadData?.id);
      if (doc?.eligible_offers) {
        return { eligible_offers: doc.eligible_offers, referral_category: doc?.referral_category || "business", lawyer_handoff_context: doc?.lawyer_handoff_context || null };
      }
    }
    // Search through messages for quick_action_buttons
    for (let i = threadData.messages.length - 1; i >= 0; i--) {
      const message = threadData.messages[i];
      if (message.message_type === "quick_actions" && message?.payload?.buttons?.length > 0) {
        // Skip the V3.5 legal pill — it ships eligible_offers: null and would
        // otherwise null out the offer for the whole thread. The always-on
        // "Consult Attorney" button in the same turn carries the real offer.
        let legalReviewButton =
          findConsultationOfferCarrier(message?.payload?.buttons) ||
          message?.payload?.buttons.find((btn) => btn.type === "legal_review_request" && !isLegalPill(btn));
        if (legalReviewButton) {
          referral_category = legalReviewButton?.referral_category || "business";
          eligible_offers = legalReviewButton?.eligible_offers;
          lawyer_handoff_context = legalReviewButton?.lawyer_handoff_context || null;
          break;
        }
      }
    }
  }
  return { eligible_offers, referral_category, lawyer_handoff_context };
};

export const getFormName = () => {
  var heading = document.getElementsByClassName("cl-headerTitle")[0]?.textContent || "Unknown";
  if (heading.toLowerCase().includes("sign in")) {
    return "signin-page";
  } else if (heading.toLowerCase().includes("sign up")) {
    return "signup-page";
  } else if (heading.toLowerCase().includes("check your email")) {
    return "otp-verification-page";
  } else if (heading.toLowerCase().includes("enter your password")) {
    return "password-page-signin-journey";
  } else {
    var heading2 = document.querySelector('h1[data-localization-key="signIn.start.title"]');
    var heading3 = document.querySelector('h1[data-localization-key="signUp.start.title"]')?.textContent;
    if (heading2) {
      if (heading2.textContent.toLowerCase().includes("sign up")) {
        if (heading3 && heading3.toLowerCase().includes("verify")) {
          return "email-verification-page";
        } else {
          return "signup-page";
        }
      }
    }
    return "unknown-page-auth-journey";
  }
};

export const openDraftIntent = (btn, threadData, userMetadata, createMessage, dispatch, socketRef, sendMessage) => {
  // 3 types of plans :free, business, enterprise
  // enterprise: no restrictions, user will perform the action. in this case, open ask to draft intent
  // subscriber / free : check if threads_remaining is 0, if yes, open popup, else open ask to draft intent

  // Event 104:
  trackAnalytics(
    EVENT_NAMES.CLICKED_BUTTON,
    {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.OPEN_DRAFT_INTENT,
      [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
      [EVENT_PARAMETERS.AUTO_TRIGGER]: true,
    },
    userMetadata,
  );
  // console.log("openDraftIntent: Setting buttonClickTypeForTrigger to 'convert_to_draft'");
  dispatch(setButtonClickTypeForTrigger("convert_to_draft"));
  var message = createMessage("chat", "add-message", {
    thread_id: threadData?.id,
    convert_to_draft: true,
    button_click_type: "convert_to_draft",
    message: btn.text,
  });
  sendWsMessage({
    message: message,
    dispatch: dispatch,
    socketRef: socketRef,
    sendMessage: sendMessage,
    onSuccess: (response) => {
      // console.log("successfully added message to thread");
    },
    onFailure: (error) => {
      // console.log("failed to add message to thread");
      dispatch(setAwaitingResponse(false));
      dispatch(setMessagingDisabled(false));
    },
  });
};

export const openDoc = (doc, metadata, dispatch, email, navigate) => {
  // Event 105:
  console.log(doc);
  trackAnalytics(
    EVENT_NAMES.CLICKED_BUTTON,
    {
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.THREAD_ID]: doc?.thread_id,
      [EVENT_PARAMETERS.BUTTON_TEXT]: "View Document",
      [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
      [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: metadata?.subscription_type || "anonymous",
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.VIEW_DOCUMENT,
      [EVENT_PARAMETERS.USER_ID]: metadata?.id,
      [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.DOCUMENT_LIBRARY,
    },
    metadata,
  );
  const docTitle = doc?.document_name || doc?.document_title || "";
  var redirectURL = `/chat/${doc?.thread_id}?view_doc=true`;
  if (doc?.document_id) {
    redirectURL += `&doc_id=${doc.document_id}&version=${doc?.version}`;
  }
  if (docTitle) {
    redirectURL += `&doc_title=${encodeURIComponent(docTitle)}`;
  }

  navigate(redirectURL, {
    state: {
      threadId: doc?.thread_id,
      threadTitle: doc?.thread_title,
      documentId: doc?.document_id,
      documentTitle: doc?.document_title,
      isDocumentLocked: doc?.is_document_locked,
    },
  });
};

// `startAccessDocCheckout` was removed in Group 9 + 10 — the document-library
// "Access Document" CTA now dispatches `openCheckoutForEntry({ entry: 'locked_doc' })`
// directly from `DocLibraryCard.handleDocAccessClick`.

export const hasAcceptEditSuggestionsButton = (chatInfo) => {
  if (chatInfo?.quick_action_buttons?.length > 0) {
    return chatInfo?.quick_action_buttons.some((btn) => btn.type === "accept_edit_suggestions");
  }
  return false;
};

export const showQuickActionButton = (
  btn,
  isLastReply,
  lawyerHubData,
  showLegalReviewRequestButton,
  lastConversationalMessage,
  chatInfo,
  changesAccepted,
) => {
  var legalReviewRequested = lawyerHubData?.some((item) => item.thread_id === chatInfo.id);
  var chatId = chatInfo?.id;

  if (btn.type === "accept_edit_suggestions" || btn.type === "quick_response") {
    if (isLastReply) {
      return true;
    }
    return false;
  }
  if (btn.type === "legal_review_request") {
    var legalReview = lawyerHubData?.find((item) => item.thread_id === chatId);
    if (legalReview) {
      return false;
    }
    if (showLegalReviewRequestButton) {
      if (legalReviewRequested) {
        return false;
      }
      // if this is the last conversational message, then show the legal review button
      return true;
    } else {
      return false;
    }
  }

  if (lastConversationalMessage === chatInfo.id) {
    if (changesAccepted) {
      return true;
    }
    if (hasAcceptEditSuggestionsButton(chatInfo)) {
      return false;
    }
    return true;
  }
};

export const updatebuttonsToShow = (
  chatInfo,
  setButtonsToShow,
  lawyerHubData,
  showLegalReviewRequestButton,
  lastConversationalMessage,
  changesAccepted,
) => {
  if (chatInfo?.quick_action_buttons?.length > 0) {
    const buttons = chatInfo?.quick_action_buttons.filter((btn) =>
      showQuickActionButton(
        btn,
        true,
        lawyerHubData,
        showLegalReviewRequestButton,
        lastConversationalMessage,
        chatInfo,
        changesAccepted,
      ),
    );
    setButtonsToShow(buttons);
    return [];
  }
};

// `getTransactionType`, `DOC_TRANSACTION_TYPES`, `getCheckoutData`,
// `initiateConsultationCheckout`, and `takeBusinessPlanUnlockedAction` were
// removed in subscription-tiers-rewrite Groups 9 + 10. Transaction-type
// strings (`ai_document`, `document_finalisation`, `legal_consultation`,
// `bundle_doc_*`) and ad-hoc URL-based checkouts are gone — all purchases
// route through `payment_type === "subscription"` via the modal-driven
// `CheckoutFlowModal` flow.
